/**
 * Multi-metric chart series helpers — ported from flank-20's chart-series.ts,
 * adapted from recharts to chart.js (flank's charting lib). Shared by the
 * Line / Area / Bar widgets when the user picks >1 metric.
 *
 * When metric formats mix (e.g. count + percent + currency), series split onto
 * two Y axes: the first format → left ('y'), the next distinct format → right
 * ('y1'). chart.js caps cleanly at two axes, same as recharts did.
 */
import type { ChartOptions } from 'chart.js';
import { type MetricFormat, getMetricDef } from './canonicalMetrics';
import { formatAxisTick, formatMetricValue, metricLabel, percentAxisDecimals } from './metricFormat';

export const MULTI_METRIC_PALETTE = ['#3b82f6', '#f59e0b', '#10b981'] as const;
export const MAX_CHART_METRICS = 3;

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  format: MetricFormat;
  /** chart.js yAxisID — 'y' (left) or 'y1' (right). */
  axis: 'y' | 'y1';
}

/** Per-metric manual axis assignment (FB-006/#4408), keyed by metric key. */
export type AxisOverrides = Record<string, 'y' | 'y1'>;

/** Read `metrics: string[]` config, falling back to legacy `metric: string`. */
export function resolveChartMetrics(config: { metrics?: unknown; metric?: unknown }): string[] {
  const arr = Array.isArray(config.metrics)
    ? (config.metrics as unknown[]).filter((v): v is string => typeof v === 'string' && v.length > 0)
    : [];
  if (arr.length > 0) return arr.slice(0, MAX_CHART_METRICS);
  if (typeof config.metric === 'string' && config.metric) return [config.metric];
  return ['impressions'];
}

/**
 * Build series descriptors. A metric with an explicit entry in `axisOverrides`
 * (#4408 — users can assign a metric to either axis by hand) uses that axis
 * directly; every other metric falls back to the automatic split-by-format
 * rule so the common case (no one has touched the control) is unchanged.
 */
export function buildSeries(metrics: string[], accent?: string, axisOverrides?: AxisOverrides): ChartSeries[] {
  const seen: MetricFormat[] = [];
  // A dashboard accent colour replaces the primary series colour; the secondary
  // colours stay so multi-metric charts remain distinguishable.
  const palette = accent ? [accent, ...MULTI_METRIC_PALETTE.slice(1)] : MULTI_METRIC_PALETTE;
  return metrics.slice(0, MAX_CHART_METRICS).map((key, i) => {
    const format = (getMetricDef(key)?.format ?? 'count') as MetricFormat;
    if (!seen.includes(format)) seen.push(format);
    return {
      key,
      label: metricLabel(key),
      color: palette[i % palette.length],
      format,
      axis: axisOverrides?.[key] ?? (seen[0] === format ? 'y' : 'y1'),
    };
  });
}

/**
 * chart.js scale config. Axes are VISIBLE: these render at widget size, not as
 * sparklines, and a trend line with no axis can't be read — you can see there
 * was a spike but not when it happened or how big it was (flank#4114, Earl:
 * "no dates on the impression trend").
 *
 * Kept quiet rather than absent: no vertical grid, a faint horizontal one, small
 * ticks, and autoSkip so a 90-day window labels a readable handful of dates
 * instead of ninety. Values use the compact tick formatter so money reads
 * "$606K" up the side rather than colliding with itself.
 */
const TICK_FONT = { size: 10 } as const;

/**
 * Tick formatter for one value axis. Precision for percentages is derived from
 * the axis's own tick set rather than fixed, so a low-CTR axis doesn't render
 * two consecutive ticks as the same "0.01%". chart.js hands the callback the
 * full tick array, which is the only place that range is known.
 */
const valueTickCallback =
  (metricKey: string) =>
  (value: string | number, _index: number, ticks: Array<{ value: number }>): string => {
    const max = ticks.reduce((m, t) => Math.max(m, Math.abs(t.value)), 0);
    return formatAxisTick(metricKey, Number(value), { percentDecimals: percentAxisDecimals(max) });
  };

/** Axis title text for the series sharing one side — one label alone, or a
 *  joined list when more than one metric shares an axis (manual override can
 *  put unrelated metrics together). Labels which axis is which (#4408). */
const axisTitle = (onAxis: ChartSeries[]): string => onAxis.map(s => s.label).join(' · ');

export function buildScales(
  series: ChartSeries[],
  opts: { normalize?: boolean } = {},
): ChartOptions<'line' | 'bar'>['scales'] {
  if (opts.normalize) {
    // Every series is plotted as % of its own max, so one shared axis reads
    // for all of them — the point of normalizing is to compare shape, not
    // magnitude. Real values still show in the tooltip (see normalizeSeries).
    return {
      x: {
        display: true,
        grid: { display: false },
        ticks: { font: TICK_FONT, autoSkip: true, maxTicksLimit: 8, maxRotation: 0 },
      },
      y: {
        display: true,
        beginAtZero: true,
        max: 100,
        position: 'left',
        title: { display: true, text: '% of series max', font: TICK_FONT },
        grid: { color: 'rgba(0, 0, 0, 0.06)' },
        ticks: { font: TICK_FONT, maxTicksLimit: 5, callback: (v: string | number) => `${v}%` },
      },
    };
  }

  const left = series.filter(s => s.axis === 'y');
  const right = series.filter(s => s.axis === 'y1');
  const leftPrimary = left[0] ?? series[0];

  return {
    x: {
      display: true,
      grid: { display: false },
      ticks: { font: TICK_FONT, autoSkip: true, maxTicksLimit: 8, maxRotation: 0 },
    },
    y: {
      display: true,
      beginAtZero: true,
      position: 'left',
      // Only labelled once there's a second axis to disambiguate from — a
      // single-axis chart doesn't need "Impressions" on the side as well as
      // in the legend.
      title: right.length > 0 ? { display: true, text: axisTitle(left), font: TICK_FONT } : undefined,
      grid: { color: 'rgba(0, 0, 0, 0.06)' },
      ticks: {
        font: TICK_FONT,
        maxTicksLimit: 5,
        callback: valueTickCallback(leftPrimary?.key ?? ''),
      },
    },
    ...(right.length > 0
      ? {
          y1: {
            display: true,
            beginAtZero: true,
            position: 'right' as const,
            title: { display: true, text: axisTitle(right), font: TICK_FONT },
            // The left axis already draws the horizontal lines; a second set
            // offset from it reads as a broken grid.
            grid: { drawOnChartArea: false },
            ticks: {
              font: TICK_FONT,
              maxTicksLimit: 5,
              callback: valueTickCallback(right[0].key),
            },
          },
        }
      : {}),
  };
}

/**
 * Scale each series to a percentage of its own peak value (#4408 normalize
 * toggle) so metrics with very different magnitudes plot on one shared axis
 * without the low-volume one flattening to a line. A series whose values are
 * all <= 0 stays at 0 rather than dividing by zero.
 */
export function normalizeSeriesValues(rows: Array<{ values: number[] }>, series: ChartSeries[]): number[][] {
  const maxes = series.map((_, i) => rows.reduce((m, r) => Math.max(m, r.values[i] ?? 0), 0));
  return rows.map(r => r.values.map((v, i) => (maxes[i] > 0 ? (v / maxes[i]) * 100 : 0)));
}

/**
 * Tooltip label callback that formats each point by its metric's format.
 * Uses `raw` (the original datum) so it's correct for lines and for bars in
 * either orientation (horizontal bars put the value on the x axis).
 *
 * `rawRows` is the pre-normalize data (#4408): when the chart is plotting
 * normalizeSeriesValues' 0-100 output, `ctx.raw` IS that percentage, not the
 * real metric value — the ticket requires the tooltip keep showing the actual
 * value, so this looks it up by point index instead when supplied.
 */
export function tooltipLabelCallback(series: ChartSeries[], rawRows?: Array<{ values: number[] }>) {
  return (ctx: { datasetIndex: number; dataIndex: number; raw: unknown }): string => {
    const s = series[ctx.datasetIndex];
    const v = rawRows
      ? (rawRows[ctx.dataIndex]?.values[ctx.datasetIndex] ?? 0)
      : typeof ctx.raw === 'number'
        ? ctx.raw
        : Number(ctx.raw ?? 0);
    return `${s?.label ?? ''}: ${formatMetricValue(s?.key ?? '', v)}`;
  };
}
